import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../App.css';
import * as THREE from 'three';
import Timeline from '../Timeline';
import { Canvas } from '@react-three/fiber';
import Earth from '../Earth';
import PlanetFocusText from '../PlanetFocusText';
import TextPositioner from '../TextPositioner';
import SceneController from '../SceneController';

function HomePage() {
  const navigate = useNavigate();

  const [planetLabels, setPlanetLabels] = useState([
    { text: "DISCOVER TALENT", position: { x: -1000, y: -1000 }, visibility: 0, isHovered: false },
    { text: "ABOUT", position: { x: -1000, y: -1000 }, visibility: 0, isHovered: false },
    { text: "FORUM", position: { x: -1000, y: -1000 }, visibility: 0, isHovered: false }
  ]);
  
  const [transitionState, setTransitionState] = useState({
    active: false,
    targetPlanetIndex: -1,
    startTime: 0,
    duration: 2500,
  });
  
  const staticStarsDataRef = useRef([]);
  const planet1Ref = useRef(null);
  const planet2Ref = useRef(null);
  const mousePositionRef = useRef({ x: window.innerWidth / 2, y: window.innerHeight / 2 });
  const smoothScrollY = useRef(0);
  const appRef = useRef(null);
  const timelineRef = useRef(null);
  const planetRefs = useRef([]);
  const lastFocusIndex = useRef(-1);

  const planetUrls = ['/discover', '/about', '/forum'];

  const handlePlanetClick = (clickedIndex) => {
    if (!transitionState.active && clickedIndex === lastFocusIndex.current) {
      const targetUrl = planetUrls[clickedIndex];
      setTransitionState({
        active: true,
        targetPlanetIndex: clickedIndex,
        startTime: performance.now(),
        duration: 2500,
      });

      // --- CHANGE #1: Navigation Delay ---
      // Navigate a bit earlier than the full animation duration.
      // This ensures we switch pages BEFORE the camera can go inside the planet.
      const navigationDelay = 2200; // Was 2500

      setTimeout(() => {
        navigate(targetUrl);
      }, navigationDelay);
    }
  };
  
  const handlePositionsUpdate = (positions) => {
    setPlanetLabels(currentLabels =>
      currentLabels.map((label, index) => ({
        ...label,
        position: positions[index] || label.position,
        visibility: index === lastFocusIndex.current ? 1 : 0,
      }))
    );
  };
  
  const handlePlanetHover = (index, isHovering) => {
    setPlanetLabels(prevLabels => prevLabels.map((label, i) => {
        if (i === index) {
            return { ...label, isHovered: isHovering };
        }
        return label;
    }));
  };
  
  useEffect(() => {
    planet1Ref.current = document.getElementById('planet');
    planet2Ref.current = document.getElementById('planet2');
    
    const allStaticStarElements = Array.from(document.querySelectorAll('.star'));
    staticStarsDataRef.current = allStaticStarElements.map(starElement => {
      const style = getComputedStyle(starElement);
      let initialTopValue = 0;
      if (style.top.includes('vh')) { initialTopValue = (parseFloat(style.top) / 100) * window.innerHeight; }
      else if (style.top.includes('px')) { initialTopValue = parseFloat(style.top); }
      else if (style.top.includes('%')) { initialTopValue = (parseFloat(style.top) / 100) * window.innerHeight; }
      if (style.getPropertyValue('bottom') && style.getPropertyValue('bottom') !== 'auto') {
          let initialBottomValue = 0;
          if (style.bottom.includes('vh')) { initialBottomValue = (parseFloat(style.bottom) / 100) * window.innerHeight; }
          else if (style.bottom.includes('px')) { initialBottomValue = parseFloat(style.bottom); }
          else if (style.bottom.includes('%')) { initialBottomValue = (parseFloat(style.bottom) / 100) * window.innerHeight; }
          initialTopValue = window.innerHeight - initialBottomValue - starElement.offsetHeight;
      }
      return {
          element: starElement, initialLeftPixels: starElement.offsetLeft, initialTopPixels: initialTopValue,
          parallaxFactor: 0.08 + Math.random() * 0.5, loopOffset: 0, height: starElement.offsetHeight,
          state: 'idle', interactionX: 0, interactionY: 0, orbitData: null, velocity: { vx: 0, vy: 0 },
      };
    });

    const recordMousePositionForPhysics = (e) => {
      mousePositionRef.current = { x: e.clientX, y: e.clientY };
    };
    document.addEventListener('mousemove', recordMousePositionForPhysics);
    recordMousePositionForPhysics({ clientX: window.innerWidth / 2, clientY: window.innerHeight / 2 });
    
    return () => {
      document.removeEventListener('mousemove', recordMousePositionForPhysics);
    };
  }, []);

  useEffect(() => {
    let animationFrameId;
    const animationLoop = () => {
      const actualScrollY = window.scrollY;
      const easing = 0.08; 
      smoothScrollY.current += (actualScrollY - smoothScrollY.current) * easing;
      const currentScrollY = smoothScrollY.current;

      if (transitionState.active) {
        lastFocusIndex.current = -1;
        const elapsedTime = performance.now() - transitionState.startTime;
        let progress = Math.min(elapsedTime / transitionState.duration, 1);
        const easedProgress = progress * progress;

        const overlay = document.querySelector('.transition-overlay');
        if (overlay) {

            const flashTriggerProgress = 0.60;
            if (progress > flashTriggerProgress) {
              overlay.style.opacity = 1;
            } else {
              overlay.style.opacity = 0;
            }
        }

        const viewportCenterX = window.innerWidth / 2;
        const viewportCenterY = window.innerHeight / 2;
        staticStarsDataRef.current.forEach(starData => {
            starData.element.classList.add('streaking');
            const rect = starData.element.getBoundingClientRect();
            const starX = rect.left + rect.width / 2;
            const starY = rect.top + rect.height / 2;
            const dx = starX - viewportCenterX;
            const dy = starY - viewportCenterY;
            const angle = Math.atan2(dy, dx);
            const moveDistance = easedProgress * (window.innerWidth / 1.5);
            const streakScale = 1 + easedProgress * 30;
            const translateX = Math.cos(angle) * moveDistance;
            const translateY = Math.sin(angle) * moveDistance;
            starData.element.style.transform = `translate(${translateX}px, ${translateY}px) rotate(${angle}rad) scaleX(${streakScale}) scaleY(1)`;
        });

      } else {
          // --- ALL ORIGINAL SCROLLING LOGIC REMAINS 100% UNTOUCHED ---
          const overlay = document.querySelector('.transition-overlay');
          if (overlay && overlay.style.opacity !== '0') {
              overlay.style.opacity = '0';
          }
        
          if(appRef.current){
            appRef.current.style.transform = `translateY(-${currentScrollY}px)`;
          }

          const totalScrollableHeight = document.body.scrollHeight - window.innerHeight;
          const scrollPercent = totalScrollableHeight > 0 ? (currentScrollY / totalScrollableHeight) * 100 : 0;
          
          if (timelineRef.current) {
            const timelineIndicator = timelineRef.current.querySelector('.timeline-indicator');
            const timelineClips = timelineRef.current.querySelectorAll('.timeline-clip');
            if (timelineIndicator) timelineIndicator.style.left = `calc(${scrollPercent}% - 1.5px)`;
            if (timelineClips) {
              timelineClips.forEach((clip, index) => {
                const startPercent = index * 33.33;
                const endPercent = (index + 1) * 33.33;
                clip.classList.toggle('active', scrollPercent >= startPercent && scrollPercent < endPercent);
              });
              if(scrollPercent >= 100 && timelineClips[2]){
                 timelineClips[2].classList.add('active');
              }
            }
          }
          
          const mouseX = mousePositionRef.current.x;
          const mouseY = mousePositionRef.current.y;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          if (planet1Ref.current) {
            const p1ParallaxFactorY = 0.3; const p1TranslateY = currentScrollY * p1ParallaxFactorY;
            const p1EllipticalAmplitudeX = viewportWidth * 0.08; const p1EllipticalFrequency = 0.0008;
            let p1TranslateX_Elliptical = Math.sin(currentScrollY * p1EllipticalFrequency) * p1EllipticalAmplitudeX * -1;
            const growthStartScrollP1 = viewportHeight * 0.05; let p1Scale = 1;
            if (currentScrollY > growthStartScrollP1) { p1Scale = 1 + ((currentScrollY - growthStartScrollP1) / (totalScrollableHeight * 0.1)); }
            p1Scale = Math.min(p1Scale, 5); const p1Rotate = currentScrollY / 230;
            planet1Ref.current.style.transform = `translateX(calc(-50% + ${p1TranslateX_Elliptical}px)) translateY(${p1TranslateY}px) scale(${p1Scale}) rotate(${p1Rotate}deg)`;
            const p1IsOnScreen = (planet1Ref.current.offsetTop + p1TranslateY < viewportHeight + planet1Ref.current.offsetHeight * p1Scale) && (planet1Ref.current.offsetTop + p1TranslateY > -planet1Ref.current.offsetHeight * p1Scale);
            planet1Ref.current.style.opacity = p1Scale > 0.05 && p1IsOnScreen ? '1' : '0';
          }
          if (planet2Ref.current) {
            if (scrollPercent >= 48) {
              const scrollOffsetForP2 = Math.max(0, currentScrollY - (totalScrollableHeight * 0.48));
              const p2ParallaxFactorY = 0.35; const p2TranslateY = scrollOffsetForP2 * p2ParallaxFactorY;
              const ellipticalAmplitudeX = viewportWidth * 0.10; const ellipticalFrequency = 0.001;
              const p2TranslateX_Elliptical = Math.sin(scrollOffsetForP2 * ellipticalFrequency) * ellipticalAmplitudeX;
              const growthStartScrollP2 = viewportHeight * 0.02; let p2Scale = 0.8;
              if (scrollOffsetForP2 > growthStartScrollP2) { p2Scale = 0.8 + ((scrollOffsetForP2 - growthStartScrollP2) / (totalScrollableHeight * 0.15)); }
              p2Scale = Math.min(p2Scale, 4.5); const p2Rotate = scrollOffsetForP2 / 220;
              planet2Ref.current.style.transform = `translateX(calc(-50% + ${p2TranslateX_Elliptical}px)) translateY(${p2TranslateY}px) scale(${p2Scale}) rotate(${p2Rotate}deg)`;
              planet2Ref.current.style.opacity = '1';
            } else {
              planet2Ref.current.style.opacity = '0';
            }
          }

          staticStarsDataRef.current.forEach(starData => {
             if (starData.element.classList.contains('streaking')) {
                 starData.element.classList.remove('streaking');
                 starData.element.style.transform = '';
             }
            const parallaxMove = -currentScrollY * starData.parallaxFactor;
            let currentYParallaxOffset = starData.loopOffset + parallaxMove;
            const visualTopPosition = starData.initialTopPixels + currentYParallaxOffset;
            const totalRange = viewportHeight + starData.height * 2;
            if (visualTopPosition < -starData.height * 1.5) { starData.loopOffset += totalRange; }
            else if (visualTopPosition > viewportHeight + starData.height * 0.5) { starData.loopOffset -= totalRange; }
            currentYParallaxOffset = starData.loopOffset + parallaxMove;
            const rect = starData.element.getBoundingClientRect(); const starX = rect.left + rect.width / 2; const starY = rect.top + rect.height / 2;
            const dx = starX - mouseX; const dy = starY - mouseY; const distance = Math.hypot(dx, dy);
            let targetInteractionX = 0; let targetInteractionY = 0;
            switch (starData.state) {
                case 'idle':
                    const orbitEnterRadius = 50; const pushAwayRadius = 120;
                    if (distance < orbitEnterRadius) {
                        starData.state = 'orbiting';
                        starData.orbitData = { angle: Math.atan2(dy, dx), radius: distance, speed: 0.03 + Math.random() * 0.04, startTime: Date.now(), duration: 2000 + Math.random() * 2000 };
                    } else if (distance < pushAwayRadius) {
                        const force = 1 - (distance / pushAwayRadius); const angle = Math.atan2(dy, dx);
                        const maxPushDistance = 40; targetInteractionX = Math.cos(angle) * force * maxPushDistance; targetInteractionY = Math.sin(angle) * force * maxPushDistance;
                    }
                    starData.interactionX += (targetInteractionX - starData.interactionX) * 0.1; starData.interactionY += (targetInteractionY - starData.interactionY) * 0.1;
                    break;
                case 'orbiting':
                    if (Date.now() - starData.orbitData.startTime > starData.orbitData.duration) {
                        starData.state = 'returning';
                        const tangentAngle = starData.orbitData.angle + Math.PI / 2; const breakawaySpeed = 3 + Math.random() * 3;
                        starData.velocity = { vx: Math.cos(tangentAngle) * breakawaySpeed, vy: Math.sin(tangentAngle) * breakawaySpeed };
                        starData.orbitData = null;
                    } else {
                        starData.orbitData.angle += starData.orbitData.speed;
                        const orbitTargetX = mouseX + Math.cos(starData.orbitData.angle) * starData.orbitData.radius; const orbitTargetY = mouseY + Math.sin(starData.orbitData.angle) * starData.orbitData.radius;
                        const starBaseX = starData.initialLeftPixels + starData.interactionX; const starBaseY = starData.initialTopPixels + currentYParallaxOffset + starData.interactionY;
                        const newInteractionX = orbitTargetX - starBaseX; const newInteractionY = orbitTargetY - starBaseY;
                        starData.interactionX += newInteractionX; starData.interactionY += newInteractionY;
                    }
                    break;
                case 'returning':
                    starData.interactionX += starData.velocity.vx; starData.interactionY += starData.velocity.vy;
                    starData.velocity.vx *= 0.97; starData.velocity.vy *= 0.97;
                    starData.interactionX += (0 - starData.interactionX) * 0.01; starData.interactionY += (0 - starData.interactionY) * 0.01;
                    if (Math.hypot(starData.interactionX, starData.interactionY) < 0.5 && Math.hypot(starData.velocity.vx, starData.velocity.vy) < 0.1) {
                        starData.interactionX = 0; starData.interactionY = 0; starData.velocity = { vx: 0, vy: 0 }; starData.state = 'idle';
                    }
                    break;
                default: break;
            }
            const finalTranslateX = starData.interactionX;
            const finalTranslateY = currentYParallaxOffset + starData.interactionY;
            starData.element.style.translate = `${finalTranslateX}px ${finalTranslateY}px`;
          });
      }

      animationFrameId = requestAnimationFrame(animationLoop);
    };

    animationLoop();
    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [transitionState, navigate]);

  const keyframes = {
      positions: [ new THREE.Vector3(0, -0.2, 0), new THREE.Vector3(4.5, -0.5, -5), new THREE.Vector3(-4.5, -0.5, -5) ],
      scales: [ new THREE.Vector3(1, 1, 1), new THREE.Vector3(0.5, 0.5, 0.5), new THREE.Vector3(0.5, 0.5, 0.5) ],
      opacities: [1.0, 0.25, 0.25] 
  };

  return (
    <>
      <div className={`transition-overlay ${transitionState.active ? 'active' : ''}`}></div>

      {planetLabels.map((label, index) => (
        <PlanetFocusText
          key={index}
          text={label.text}
          position={label.position}
          visibility={label.visibility}
          isHovered={label.isHovered}
        />
      ))}

      <div className="full-app" ref={appRef}>
        <main className="content">
          <div className="editor"></div>
        </main>
      </div>
       
        <div className="earth-canvas">
          <Canvas camera={{ position: [0, 0, 5], fov: 60 }}>
            <SceneController
                transitionState={transitionState}
                planetRefs={planetRefs}
                keyframes={keyframes}
                smoothScrollY={smoothScrollY}
                lastFocusIndex={lastFocusIndex}
            />
            {[...Array(3).keys()].map(i => (
              <Earth 
                key={i} 
                colorIndex={i} 
                ref={el => planetRefs.current[i] = el}
                index={i}
                onPlanetClick={handlePlanetClick}
                onPlanetHover={handlePlanetHover}
              />
            ))}
            <TextPositioner 
              planetRefs={planetRefs}
              onPositionsUpdate={handlePositionsUpdate}
            />
          </Canvas>
        </div>
      <Timeline ref={timelineRef} />

      <div id="planet"></div>
      <div id="planet2"></div>
      <div id="star1" className="star"></div><div id="star2" className="star"></div><div id="star3" className="star"></div><div id="star4" className="star"></div><div id="star5" className="star"></div><div id="star6" className="star"></div><div id="star7" className="star"></div><div id="star8" className="star"></div><div id="star9" className="star"></div><div id="star10" className="star"></div><div id="star11" className="star"></div><div id="star12" className="star"></div><div id="star13" className="star"></div><div id="star14" className="star"></div><div id="star15" className="star"></div><div id="star16" className="star"></div><div id="star17" className="star"></div><div id="star18" className="star"></div><div id="star19" className="star"></div><div id="star20" className="star"></div><div id="star21" className="star"></div>
    </>
  );
}

export default HomePage;
